<script setup lang="ts">
/* =============================================================
   TEXT FIELD
   =============================================================
   Label + input + hint/error, wired together with ids so screen
   readers announce the whole group. Password fields get a reveal
   toggle for free.

   `inheritAttrs: false` so that placeholder, autocomplete, name,
   required and friends land on the <input> rather than the
   wrapper — only `class` stays on the wrapper.
   ============================================================= */

import { computed, ref, useAttrs, useId } from 'vue'
import Icon from '@/components/Icon.vue'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    label: string
    hint?: string
    error?: string
    type?: string
    modelValue?: string
  }>(),
  { type: 'text', modelValue: '' },
)

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const attrs = useAttrs()
const id = useId()
const hintId = `${id}-hint`
const errorId = `${id}-error`
const revealed = ref(false)

const isPassword = computed(() => props.type === 'password')
const inputType = computed(() => (isPassword.value && revealed.value ? 'text' : props.type))

const rootClass = computed(() => attrs.class as string | undefined)

const inputAttrs = computed(() => {
  const rest: Record<string, unknown> = { ...attrs }
  delete rest.class
  delete rest.style
  return rest
})

const describedBy = computed(() => {
  const ids = [props.error ? errorId : null, props.hint ? hintId : null].filter(Boolean)
  return ids.length > 0 ? ids.join(' ') : undefined
})
</script>

<template>
  <div class="field" :class="rootClass">
    <div class="labelRow">
      <label :for="id" class="label">{{ label }}</label>
      <div v-if="$slots.action" class="action"><slot name="action" /></div>
    </div>

    <div class="controlWrap">
      <input
        v-bind="inputAttrs"
        :id="id"
        :type="inputType"
        :value="modelValue"
        class="control"
        :class="{ controlError: error, controlWithButton: isPassword }"
        :aria-invalid="error ? true : undefined"
        :aria-describedby="describedBy"
        @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
      />

      <button
        v-if="isPassword"
        type="button"
        class="reveal"
        :aria-label="revealed ? 'Hide password' : 'Show password'"
        tabindex="-1"
        @click="revealed = !revealed"
      >
        <Icon :name="revealed ? 'eyeOff' : 'eye'" :size="18" />
      </button>
    </div>

    <p v-if="error" :id="errorId" class="error">
      <Icon name="alert" :size="14" />
      {{ error }}
    </p>
    <p v-else-if="hint" :id="hintId" class="hint">{{ hint }}</p>
  </div>
</template>

<style scoped>
.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-3xs);
}

.labelRow {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-2xs);
}

.label {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--color-heading);
}

.action {
  font-size: var(--text-xs);
}

.controlWrap {
  position: relative;
  display: flex;
}

.control {
  width: 100%;
  height: var(--control-height);
  padding-inline: var(--control-pad-x);
  font-size: var(--text-sm);
  color: var(--color-text);
  background-color: var(--color-surface);
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-sm);
  transition:
    border-color var(--dur-fast) var(--ease),
    box-shadow var(--dur-fast) var(--ease);
}

.control::placeholder {
  color: var(--color-text-faint);
}

.control:hover:not(:disabled) {
  border-color: var(--color-text-faint);
}

.control:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: var(--shadow-focus);
}

.control:disabled {
  background-color: var(--color-surface-sunken);
  cursor: not-allowed;
}

.controlWithButton {
  padding-inline-end: calc(var(--control-height) - var(--space-3xs));
}

.controlError {
  border-color: var(--color-danger);
}

.controlError:focus {
  box-shadow: 0 0 0 3px var(--color-danger-soft);
}

.reveal {
  position: absolute;
  inset-block: 0;
  inset-inline-end: 0;
  display: grid;
  place-items: center;
  width: var(--control-height);
  color: var(--color-text-faint);
  background: none;
  border: 0;
  border-radius: var(--radius-sm);
}

.reveal:hover {
  color: var(--color-text);
}

.hint,
.error {
  display: flex;
  align-items: center;
  gap: var(--space-3xs);
  font-size: var(--text-xs);
  line-height: var(--leading-snug);
}

.hint {
  color: var(--color-text-faint);
}

.error {
  color: var(--color-danger);
}

.error svg {
  flex: none;
}
</style>
